/**
 * Session-scoped persistence for the working beam model.
 *
 * Keeps the in-progress model alive across in-app navigation (e.g. visiting
 * Structura-Bits and returning to the Beam Designer) without surviving a tab
 * close. Keyed per material so the three material workspaces never collide.
 *
 * Persistence is a convenience: if sessionStorage is unavailable (private mode)
 * or full, we warn and continue rather than crash the active design session.
 */

import { SMBeamModel, BeamMaterial } from './types'

const MODEL_STORAGE_KEY_PREFIX = 'beamModel'

// Shared with the Dashboard handoff that loads an example into the workspace.
export const BEAM_TEMPLATE_STORAGE_KEY = 'beamTemplate'

function modelStorageKey(material: BeamMaterial): string {
  return `${MODEL_STORAGE_KEY_PREFIX}:${material}`
}

export function isValidBeamModel(value: unknown): value is SMBeamModel {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const candidate = value as Partial<SMBeamModel>
  return Array.isArray(candidate.nodes)
    && Array.isArray(candidate.spans)
    && Array.isArray(candidate.loadCases)
    && typeof candidate.crossSection === 'object'
    && candidate.crossSection !== null
}

export function loadPersistedModel(material: BeamMaterial): SMBeamModel | null {
  const key = modelStorageKey(material)
  let stored: string | null
  try {
    stored = sessionStorage.getItem(key)
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error)
    console.warn(`Skipped restoring persisted beam model for material '${material}': sessionStorage is unavailable (${reason}).`)
    return null
  }
  if (!stored) {
    return null
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(stored)
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error)
    console.warn(`Discarded persisted beam model for sessionStorage key '${key}': value was not valid JSON (${reason}).`)
    sessionStorage.removeItem(key)
    return null
  }
  if (!isValidBeamModel(parsed)) {
    console.warn(`Discarded persisted beam model for sessionStorage key '${key}': stored value is not a valid beam model (missing nodes/spans/loadCases/crossSection).`)
    sessionStorage.removeItem(key)
    return null
  }
  return parsed
}

export function persistModel(material: BeamMaterial, model: SMBeamModel): void {
  const key = modelStorageKey(material)
  try {
    sessionStorage.setItem(key, JSON.stringify(model))
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error)
    console.warn(`Failed to persist beam model to sessionStorage key '${key}': ${reason}. The current design is unaffected but will not survive navigation.`)
  }
}
